'use client';

// Dropdown.tsx
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useRef,
  useState,
  ComponentPropsWithoutRef,
  ReactNode,
  useEffect,
  useLayoutEffect,
} from 'react';

import classNames from 'classnames';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-client';
import { createPortal } from 'react-dom';

import { useElementPosition } from '~/hooks/useElementPosition';

import styles from './style.module.css';

// コンテキストの型定義
interface DropdownContextProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  triggerPosition: { top: number; left: number };
  setTriggerPosition: Dispatch<SetStateAction<{ top: number; left: number }>>;
  isInUpperHalf: boolean;
  triggerSize: { width: number; height: number };
  setTriggerSize: Dispatch<SetStateAction<{ width: number; height: number }>>;
}

// コンテキストの作成
const DropdownContext = createContext<DropdownContextProps>({
  isOpen: false,
  setIsOpen: () => {},
  triggerPosition: { top: 0, left: 0 },
  triggerSize: { width: 0, height: 0 },
  setTriggerPosition: () => {},
  isInUpperHalf: false,
  setTriggerSize: () => {},
});

// メインのDropdownコンポーネント
const Dropdown = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isInUpperHalf } = useElementPosition(dropdownRef);

  const [triggerPosition, setTriggerPosition] = useState({ top: 0, left: 0 });
  const [triggerSize, setTriggerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // ドロップダウン外のクリックで閉じるロジック
    if (isOpen) {
      const handleClickOutside = () => {
        setIsOpen(false);
      };
      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
    // isOpenがfalseの場合でも明示的にundefinedを返す
    return undefined;
  }, [isOpen]);

  return (
    <DropdownContext.Provider
      value={{
        isOpen,
        setIsOpen,
        isInUpperHalf,
        triggerPosition,
        setTriggerPosition,
        triggerSize,
        setTriggerSize,
      }}
    >
      <div
        ref={dropdownRef}
        className={classNames(styles.dropdown, className)}
        tabIndex={0}
        {...props}
      >
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

// Triggerコンポーネント
const Trigger = ({
  children,
  as = 'button',
  disabled = false,
}: {
  children: ReactNode;
  as?: 'button' | 'div';
  disabled?: boolean;
}) => {
  const { setIsOpen, setTriggerPosition, setTriggerSize } =
    useContext(DropdownContext);

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    // activeElement はフォーカスされていないと body 等になり、メニュー座標がずれて
    // 見た目とクリック判定が一致しない（下の項目ほど外れやすい）。トリガー自身の矩形を使う。
    const triggerRect = e.currentTarget.getBoundingClientRect();
    if (setTriggerPosition) {
      setTriggerPosition({
        top: triggerRect.bottom,
        left: triggerRect.left,
      });
    }

    if (setTriggerSize) {
      setTriggerSize({
        width: triggerRect.width,
        height: triggerRect.height,
      });
    }
    setIsOpen((prev) => !prev);
  };

  return as === 'button' ? (
    <button
      type='button'
      disabled={disabled}
      onClick={handleClick}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: '100%',
      }}
    >
      {children}
    </button>
  ) : (
    <div
      role='button'
      aria-disabled={disabled}
      onClick={handleClick}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        opacity: disabled ? 0.6 : undefined,
      }}
    >
      {children}
    </div>
  );
};

const RightClickTrigger = ({
  children,
  as = 'button',
}: {
  children: ReactNode;
  as?: 'button' | 'div';
}) => {
  const { setIsOpen, setTriggerPosition } = useContext(DropdownContext);

  return as === 'button' ? (
    <button
      type='button'
      onContextMenu={(e) => {
        e.preventDefault();
        // handleClick(e);
        // 右クリックで開く場合は、クリック位置に表示するために位置を更新
        if (setTriggerPosition) {
          setTriggerPosition({ top: e.clientY, left: e.clientX });
        }
        setIsOpen((isOpen) => !isOpen);
      }}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: '100%',
      }}
    >
      {children}
    </button>
  ) : (
    <div
      role='button'
      onContextMenu={(e) => {
        e.preventDefault();
        setIsOpen((isOpen) => !isOpen);
      }}
      style={{
        cursor: 'pointer',
        width: '100%',
      }}
    >
      {children}
    </div>
  );
};

// Bodyコンポーネント
const Body = ({
  children,
  closeOnClickItem = false, // アイテムクリック時にドロップダウンを閉じるかどうか
  width,
}: ComponentPropsWithoutRef<'div'> & {
  closeOnClickItem?: boolean;
  width?: number | string;
}) => {
  const {
    isOpen,
    setIsOpen,
    triggerPosition,
    setTriggerPosition,
    triggerSize,
  } = useContext(DropdownContext);

  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // ドロップダウンが開いた際の位置調整
    if (isOpen && bodyRef.current) {
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const maxHeight = window.innerHeight - 20; // 画面の高さから20pxのマージンを引く

      // 下辺が画面外に出る場合の調整
      if (bodyRect.bottom > window.innerHeight) {
        setTriggerPosition((prev) => ({
          ...prev,
          top: triggerPosition.top - bodyRect.height - triggerSize.height,
        }));
      }

      // 上辺が画面外に出る場合の調整
      if (bodyRect.top < 0) {
        setTriggerPosition((prev) => ({
          ...prev,
          top: 0, // 画面の最上部に配置
        }));
      }

      // 右辺が画面外に出る場合の調整
      if (bodyRect.right > window.innerWidth) {
        setTriggerPosition((prev) => ({
          ...prev,
          left: triggerPosition.left - (bodyRect.right - window.innerWidth),
        }));
      }

      // 左辺が画面外に出る場合の調整
      if (bodyRect.left < 0) {
        setTriggerPosition((prev) => ({
          ...prev,
          left: 0, // 画面の最左部に配置
        }));
      }

      // 高さが画面を超える場合の調整
      if (bodyRect.height > maxHeight) {
        // 最大高さを設定
        if (bodyRef.current) {
          bodyRef.current.style.maxHeight = `${maxHeight}px`;
        }

        // 上辺が画面外に出る場合は、下から表示するように調整
        if (bodyRect.top < 0) {
          setTriggerPosition((prev) => ({
            ...prev,
            top: window.innerHeight - maxHeight - 10, // 画面下部に10pxのマージンを残して配置
          }));
        }
      }
    }
  }, [isOpen, setTriggerPosition, triggerSize, triggerPosition]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (closeOnClickItem) {
      // setIsOpenをsetTimeoutで遅延させることで、内部のクリックイベントが先に処理されるようにする
      setTimeout(() => {
        setIsOpen(false);
      }, 0);
    }
    // クリックイベント自体はキャンセルしない
  };

  const dropdownContent = (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          ref={bodyRef}
          className={styles.body}
          onClick={handleClick}
          style={{
            width: width || 'auto',
            top: triggerPosition.top,
            left: triggerPosition.left,
            transformOrigin: 'top center',
          }}
          initial={{ opacity: 0, y: -2 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.1 }}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return mounted ? createPortal(dropdownContent, document.body) : null;
};

export const useDropdown = () => useContext(DropdownContext);

// コンポーネントにサブコンポーネントを追加
Dropdown.Trigger = Trigger;
Dropdown.RightClickTrigger = RightClickTrigger;
Dropdown.Body = Body;

export default Dropdown;
